// Distribution Zustand Store

import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {
  DistributionRecord,
  DistributionMarketer,
  DistributionProduct,
  AvailableSerial,
  DistributionFilters,
  DistributionStatus
} from '../models/distribution.model';
import {Pagination} from '../../../Data/types/BaseResponse';

interface DistributionState {
  // Data State
  distributionHistory: DistributionRecord[];
  marketers: DistributionMarketer[];
  products: DistributionProduct[];
  availableSerials: AvailableSerial[]; // For marketer delivery (product-specific serials)
  productAvailableSerials: AvailableSerial[]; // For marketer delivery tab (product-specific)

  // UI State
  isLoading: boolean;
  error: string | null;
  filters: DistributionFilters;
  selectedRecord: DistributionRecord | null;
  selectedProductId: string | null;

  // Pagination - Using unified BaseResponse.Pagination
  pagination: Pagination | undefined;

  // Actions
  setDistributionHistory: (records: DistributionRecord[]) => void;
  setMarketers: (marketers: DistributionMarketer[]) => void;
  setProducts: (products: DistributionProduct[]) => void;
  setAvailableSerials: (serials: AvailableSerial[]) => void;
  setProductAvailableSerials: (serials: AvailableSerial[]) => void;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setFilters: (filters: Partial<DistributionFilters>) => void;
  setSelectedRecord: (record: DistributionRecord | null) => void;
  setSelectedProductId: (productId: string | null) => void;

  setPagination: (pagination: Pagination | undefined) => void;

  // Transaction Actions
  removeTransaction: (transactionId: string) => void;

  // Computed Properties
  pendingDeliveries: () => DistributionRecord[];
  completedDeliveries: () => DistributionRecord[];
  filteredRecords: () => DistributionRecord[];

  // State Reset
  resetFilters: () => void;
  resetStore: () => void;
}

const initialFilters: DistributionFilters = {
  marketerId: undefined,
  productId: undefined,
  status: undefined,
  deliveredBy: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  hasSerial: undefined,
  location: undefined,
  minQuantity: undefined,
  maxQuantity: undefined
};

export const useDistributionStore = create<DistributionState>()(
  devtools(
    (set, get) => ({
      // Initial State
      distributionHistory: [],
      marketers: [],
      products: [],
      availableSerials: [],
      productAvailableSerials: [],

      isLoading: false,
      error: null,
      filters: initialFilters,
      selectedRecord: null,
      selectedProductId: null,

      pagination: undefined,

      // Actions
      setDistributionHistory: (records) =>
        set({distributionHistory: records}, false, 'setDistributionHistory'),

      setMarketers: (marketers) =>
        set({marketers}, false, 'setMarketers'),

      setProducts: (products) =>
        set({products}, false, 'setProducts'),

      setAvailableSerials: (serials) =>
        set({availableSerials: serials}, false, 'setAvailableSerials'),

      setProductAvailableSerials: (serials) =>
        set({productAvailableSerials: serials}, false, 'setProductAvailableSerials'),

      setLoading: (loading) =>
        set({isLoading: loading}, false, 'setLoading'),

      setError: (error) =>
        set({error}, false, 'setError'),

      clearError: () =>
        set({error: null}, false, 'clearError'),

      setFilters: (newFilters) =>
        set(
          (state) => ({filters: {...state.filters, ...newFilters}}),
          false,
          'setFilters'
        ),

      setSelectedRecord: (record) =>
        set({selectedRecord: record}, false, 'setSelectedRecord'),

      setSelectedProductId: (productId) =>
        set({selectedProductId: productId}, false, 'setSelectedProductId'),

      setPagination: (pagination) =>
        set({pagination}, false, 'setPagination'),

      // Transaction Actions
        removeTransaction: (transactionId) =>
          set(
            (state) => ({
              distributionHistory: state.distributionHistory.filter(
                record => record.id !== transactionId
              ),
              // Update pagination total if it exists
              pagination: state.pagination ? {
                ...state.pagination,
                total: Math.max(0, state.pagination.total - 1)
              } : undefined
            }),
            false,
            'removeTransaction'
          ),

      // Computed Properties
      pendingDeliveries: () => {
        const {distributionHistory} = get();
        return distributionHistory.filter(record =>
          record.status === DistributionStatus.PENDING
        );
      },

      completedDeliveries: () => {
        const {distributionHistory} = get();
        return distributionHistory.filter(record =>
          record.status === DistributionStatus.DELIVERED
        );
      },

      filteredRecords: () => {
        const {distributionHistory, filters} = get();

        return distributionHistory.filter(record => {
          // Marketer filter
          if (filters.marketerId && record.marketerId !== filters.marketerId) {
            return false;
          }

          // Product filter
          if (filters.productId && record.productId !== filters.productId) {
            return false;
          }

          // Status filter
          if (filters.status && record.status !== filters.status) {
            return false;
          }

          // Date range filter
          if (filters.dateFrom && new Date(record.deliveredAt) < new Date(filters.dateFrom)) {
            return false;
          }

          if (filters.dateTo && new Date(record.deliveredAt) > new Date(filters.dateTo)) {
            return false;
          }

          // Serial filter
          if (filters.hasSerial !== undefined && record.hasSerial !== filters.hasSerial) {
            return false;
          }

          // Quantity range filters
          if (filters.minQuantity !== undefined && record.quantity < filters.minQuantity) {
            return false;
          }

          if (filters.maxQuantity !== undefined && record.quantity > filters.maxQuantity) {
            return false;
          }

          return true;
        });
      },

      // Reset Functions
      resetFilters: () =>
        set({filters: initialFilters}, false, 'resetFilters'),

      resetStore: () =>
        set({
          distributionHistory: [],
          marketers: [],
          products: [],
          availableSerials: [],
          productAvailableSerials: [],
          isLoading: false,
          error: null,
          filters: initialFilters,
          selectedRecord: null,
          selectedProductId: null,
          pagination: undefined
        }, false, 'resetStore')
    }),
    {
      name: 'distribution-store'
    }
  )
);
